import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from './config/api';
import { productsAPI } from '../services/api';
import type { ProductSize, Product as SharedProduct, Category as SharedCategory, BasketItem as SharedBasketItem, POSTerminal } from '../types/order';
import { useVatLookup } from '../hooks/useVatLookup';
import { hidesSizeName, isSizeSellable } from '../utils/sellableSizes';
import { defaultSize, feeUnitGross, isFeeProduct, lineUnitPrice, productDisplayPrice } from '../utils/pricing';
import {
  ShoppingCart, Plus, Minus, X, CreditCard, Settings,
  ChevronLeft, Trash2, Check, Loader2, AlertCircle, ArrowRight,
  Receipt, FileText, Search, UtensilsCrossed, Lock, LogOut, Delete, Nfc,
  ShoppingBag, LayoutGrid, Sparkles, RotateCcw
} from 'lucide-react';
import './SelfCheckoutKiosk.css';
import KioskWelcome from './KioskWelcome';
import KioskBranding from './KioskBranding';

// Kiosk-specific type extensions
interface Product extends SharedProduct { is_kiosk_recommended?: boolean; }
type Category = SharedCategory;
type BasketItem = Omit<SharedBasketItem, 'removedIngredientNames' | 'specialInstructions' | 'discount' | 'weight' | 'weightUnit'>;

// Translations
const translations = {
  el: {
    touchToStart: 'Αγγίξτε για να ξεκινήσετε', welcomeMessage: 'Καλώς ήρθατε!',
    yourOrder: 'Η Παραγγελία σας', emptyBasket: 'Το καλάθι είναι άδειο', total: 'Σύνολο', vat: 'ΦΠΑ', clear: 'Καθαρισμός',
    selectSize: 'Επιλέξτε μέγεθος', regularSize: 'Κανονικό', customize: 'Προσαρμογή', ingredients: 'Υλικά', extras: 'Έξτρα', addToOrder: 'Προσθήκη', cancel: 'Ακύρωση',
    proceed: 'Συνέχεια', processing: 'Επεξεργασία...', waitingForCard: 'Περιμένουμε την κάρτα...', presentCard: 'Τοποθετήστε την κάρτα στο τερματικό',
    paymentSuccess: 'Επιτυχής πληρωμή!', paymentFailed: 'Η πληρωμή απέτυχε', tryAgain: 'Δοκιμάστε ξανά',
    receipt: 'Απόδειξη', invoice: 'Τιμολόγιο', enterVat: 'Εισάγετε ΑΦΜ', lookupVat: 'Αναζήτηση', vatNotFound: 'Δεν βρέθηκε ΑΦΜ',
    payNow: 'Πληρωμή', reviewOrder: 'Η Παραγγελία σας', recommended: 'Προτεινόμενα', addMore: 'Θέλετε κάτι ακόμα;',
    thankYou: 'Ευχαριστούμε!', orderNumber: 'Αριθμός παραγγελίας', orderReady: 'Θα σας καλέσουμε όταν είναι έτοιμη',
    settings: 'Ρυθμίσεις', enterPin: 'Εισάγετε PIN', logout: 'Αποσύνδεση', wrongPin: 'Λάθος PIN',
    sessionExpiring: 'Είστε ακόμα εδώ;', continueOrder: 'Συνέχεια', startOver: 'Νέα παραγγελία',
    back: 'Πίσω', confirm: 'Επιβεβαίωση', items: 'προϊόντα', item: 'προϊόν', noProducts: 'Δεν βρέθηκαν προϊόντα',
    allCategories: 'Όλα',
    noTerminalConfigured: 'Δεν έχει ρυθμιστεί POS terminal',
    lookupVatFirst: 'Παρακαλώ αναζητήστε πρώτα το ΑΦΜ',
    kioskCustomer: 'Πελάτης Kiosk',
    kioskDisabledTitle: 'Το Kiosk δεν είναι ενεργοποιημένο',
    kioskDisabledDesc: 'Το self-checkout kiosk δεν είναι ενεργοποιημένο για αυτό το κατάστημα. Επικοινωνήστε με τον διαχειριστή σας για ενεργοποίηση.',
    backToLogin: 'Επιστροφή στη Σύνδεση',
    vatNumberLabel: 'ΑΦΜ',
    // ── νέα κλειδιά της ανασχεδιασμένης οθόνης ──
    selfService: 'Αυτόματη παραγγελία',
    subtotal: 'Καθαρή αξία',
    summaryTitle: 'Σύνοψη',
    documentTitle: 'Παραστατικό',
    optionsTitle: 'Επιλογές',
    orderIsEmpty: 'Δεν έχετε προσθέσει κάτι ακόμη',
    idleText: 'Η παραγγελία θα διαγραφεί σε',
    seconds: 'δευτερόλεπτα',
    popular: 'Προτείνουμε',
    staffOnly: 'Μόνο για το προσωπικό',
    newOrderSoon: 'Επιστροφή στην αρχική…',
    goodMorning: 'Καλημέρα', goodAfternoon: 'Καλησπέρα', goodEvening: 'Καλό βράδυ',
    whatToday: 'Τι θα πάρετε σήμερα;',
    enjoy: 'Καλή όρεξη!',
    terminalLabel: 'Τερματικό',
    dontRemoveCard: 'Μην απομακρύνετε την κάρτα',
    welcomeTitle: 'Η επόμενη', welcomeAccent: 'αγαπημένη σας', welcomeEnd: 'παραγγελία.',
    welcomeDesc: 'Ό,τι τραβάει η όρεξή σας. Ακριβώς όπως το θέλετε.',
    startOrder: 'Ξεκινήστε εδώ', menuLabel: 'Το μενού μας', categoriesLabel: 'Κατηγορίες',
    chooseStep: 'Επιλέξτε', reviewStep: 'Ελέγξτε', payStep: 'Πληρώστε',
    menuSubtitle: 'Βρείτε κάτι που θα αγαπήσετε.', madeForYou: 'Στα μέτρα σας.',
    menuBanner: 'Τα αγαπημένα σας, με τον δικό σας τρόπο.',
    menuBannerDesc: 'Επιλέξτε, προσαρμόστε και απολαύστε.',
    basketHint: 'Διαλέξτε κάτι από το μενού για να ξεκινήσετε.',
    cardOnly: 'Πληρωμή με κάρτα', viewOrder: 'Δείτε την παραγγελία',
    added: 'στο καλάθι', increase: 'Αύξηση ποσότητας', decrease: 'Μείωση ποσότητας',
    remove: 'Αφαίρεση', includedVat: 'Περιλαμβάνεται ΦΠΑ', resetOrder: 'Από την αρχή',
    resetTitle: 'Να ξεκινήσουμε από την αρχή;', resetDesc: 'Τα προϊόντα της παραγγελίας σας θα αφαιρεθούν.',
    browseMenu: 'Ανακαλύψτε το μενού', customizeHint: 'Κάντε το ακριβώς όπως σας αρέσει.',
    addedToBag: 'Προστέθηκε!', choosePrompt: 'Διάλεξε τα αγαπημένα σου.', browseHint: 'Ένα άγγιγμα για να προσθέσεις ή να προσαρμόσεις.',
    yourBag: 'Η παραγγελία σου', startHere: 'Ξεκίνα με κάτι που σου αρέσει.'
  },
  en: {
    touchToStart: 'Touch to start', welcomeMessage: 'Welcome!',
    yourOrder: 'Your Order', emptyBasket: 'Your basket is empty', total: 'Total', vat: 'VAT', clear: 'Clear',
    selectSize: 'Select size', regularSize: 'Regular', customize: 'Customize', ingredients: 'Ingredients', extras: 'Extras', addToOrder: 'Add to order', cancel: 'Cancel',
    proceed: 'Continue', processing: 'Processing...', waitingForCard: 'Waiting for card...', presentCard: 'Please present your card',
    paymentSuccess: 'Payment successful!', paymentFailed: 'Payment failed', tryAgain: 'Try again',
    receipt: 'Receipt', invoice: 'Invoice', enterVat: 'Enter VAT number', lookupVat: 'Lookup', vatNotFound: 'VAT not found',
    payNow: 'Pay Now', reviewOrder: 'Your Order', recommended: 'Recommended', addMore: 'Want something else?',
    thankYou: 'Thank you!', orderNumber: 'Order number', orderReady: 'We will call you when ready',
    settings: 'Settings', enterPin: 'Enter PIN', logout: 'Logout', wrongPin: 'Wrong PIN',
    sessionExpiring: 'Still there?', continueOrder: 'Continue', startOver: 'Start over',
    back: 'Back', confirm: 'Confirm', items: 'items', item: 'item', noProducts: 'No products found',
    allCategories: 'All',
    noTerminalConfigured: 'No POS terminal configured',
    lookupVatFirst: 'Please look up the VAT number first',
    kioskCustomer: 'Kiosk Customer',
    kioskDisabledTitle: 'Kiosk is not enabled',
    kioskDisabledDesc: 'The self-checkout kiosk is not enabled for this store. Please contact your administrator to enable it.',
    backToLogin: 'Back to Login',
    vatNumberLabel: 'VAT',
    selfService: 'Self-service ordering',
    subtotal: 'Subtotal',
    summaryTitle: 'Summary',
    documentTitle: 'Document',
    optionsTitle: 'Options',
    orderIsEmpty: 'You have not added anything yet',
    idleText: 'Your order will be cleared in',
    seconds: 'seconds',
    popular: 'Popular',
    staffOnly: 'Staff only',
    newOrderSoon: 'Returning to start…',
    goodMorning: 'Good morning', goodAfternoon: 'Good afternoon', goodEvening: 'Good evening',
    whatToday: 'What can we get you today?',
    enjoy: 'Enjoy your meal!',
    terminalLabel: 'Terminal',
    dontRemoveCard: 'Please do not remove your card',
    welcomeTitle: 'Your next', welcomeAccent: 'favourite', welcomeEnd: 'order.',
    welcomeDesc: 'Whatever you are craving. Just the way you like it.',
    startOrder: 'Start your order', menuLabel: 'Our menu', categoriesLabel: 'Categories',
    chooseStep: 'Choose', reviewStep: 'Review', payStep: 'Pay',
    menuSubtitle: 'Find your new favourite.', madeForYou: 'Make it yours.',
    menuBanner: 'Your favourites. Your way.', menuBannerDesc: 'Choose, customise and enjoy.',
    basketHint: 'Choose something from the menu to get started.',
    cardOnly: 'Pay by card', viewOrder: 'View your order',
    added: 'in your bag', increase: 'Increase quantity', decrease: 'Decrease quantity',
    remove: 'Remove', includedVat: 'VAT included', resetOrder: 'Start again',
    resetTitle: 'Start a new order?', resetDesc: 'The items in your current order will be removed.',
    browseMenu: 'Explore the menu', customizeHint: 'Just the way you like it.',
    addedToBag: 'Added!', choosePrompt: 'Pick your favourites.', browseHint: 'Tap to add or make it your own.',
    yourBag: 'Your order', startHere: 'Start with something you love.'
  }
};

type Language = 'el' | 'en';
type Screen = 'attract' | 'menu' | 'customize' | 'review' | 'payment' | 'success' | 'settings';

const money = (n: number) => `€${Number(n || 0).toFixed(2)}`;

// Απόφαση Ε1(α): το kiosk χρεώνει ό,τι το ταμείο take away — ίδιες συναρτήσεις τιμής με το
// ταμείο (utils/pricing), ποτέ ωμή τιμή προϊόντος + προσαύξηση. Αλλιώς ένα κατάστημα στο
// μοντέλο «η τιμή ζει στο μέγεθος» θα έδειχνε 0,00 στο πλακίδιο, και όπου υπάρχει τιμή
// καναλιού take away ο πελάτης θα πλήρωνε άλλο ποσό από τον ταμία.
// Εξάγονται για το test που τα συγκρίνει με το ταμείο σε όλο το pricing_cases.json.
const KIOSK_CHANNEL = 'take_away' as const;
export const kioskTilePrice = (product: Product): number => productDisplayPrice(product, KIOSK_CHANNEL);
export const kioskLinePrice = (product: Product, size: ProductSize | null): number => lineUnitPrice(product, size, KIOSK_CHANNEL);
// Τα μεγέθη που πουλά το ταμείο (is_available NULL = διαθέσιμο). Με το παλιό `s.is_available`
// ένα μέγεθος NULL έλειπε από τα κουμπιά — κι αν ήταν το προεπιλεγμένο, χρεωνόταν αθέατο.
export const kioskSizeOptions = (product: Product): ProductSize[] => (product.sizes || []).filter(isSizeSellable);
const toCents = (n: number) => Math.round(Number(n || 0) * 100);

/** Missing or broken catalogue photos share the same quiet, local fallback. */
const Thumb: React.FC<{ src?: string; alt: string; icon?: number }> = ({ src, alt, icon = 28 }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return src && !failed
    ? <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
    : <div className="k-media-empty" role="img" aria-label={alt}><UtensilsCrossed style={{ width: icon, height: icon }} strokeWidth={1.25} /></div>;
};

const SelfCheckoutKiosk: React.FC = () => {
  const { store, employee, logout: authLogout } = useAuth();
  const [language, setLanguage] = useState<Language>('el');
  const t = translations[language];

  const [screen, setScreen] = useState<Screen>('attract');
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [basket, setBasket] = useState<BasketItem[]>([]);

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedSize, setSelectedSize] = useState<ProductSize | null>(null);
  const [removedIngredients, setRemovedIngredients] = useState<number[]>([]);
  const [addedExtras, setAddedExtras] = useState<{ raw_material_id: number; name: string; quantity: number; price: number }[]>([]);
  const [productIngredients, setProductIngredients] = useState<any[]>([]);
  const [availableExtras, setAvailableExtras] = useState<any[]>([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);

  const [terminal, setTerminal] = useState<POSTerminal | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [paymentMessage, setPaymentMessage] = useState('');
  const [successOrderCode, setSuccessOrderCode] = useState('');

  const [documentType, setDocumentType] = useState<'receipt' | 'invoice'>('receipt');
  const { vatNumber: invoiceVatNumber, setVatNumber: setInvoiceVatNumber, customer: invoiceCustomer, setCustomer: setInvoiceCustomer, isLoading: lookingUpVat, error: vatLookupError, setError: setVatLookupError, lookup: vatLookup, reset: resetVatLookup } = useVatLookup(); // eslint-disable-line @typescript-eslint/no-unused-vars

  const [ecrEnabled, setEcrEnabled] = useState(false);
  const [documentTypes, setDocumentTypes] = useState<any[]>([]);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = useState<number | null>(null);

  const [settingsPin, setSettingsPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [idleSeconds, setIdleSeconds] = useState(0);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const cartTarget = useRef<HTMLButtonElement>(null);
  const additionSequence = useRef(0);
  const [addition, setAddition] = useState<{ id: number; product: Product; x: number; y: number; targetX: number; targetY: number } | null>(null);

  useEffect(() => {
    if (!addition) return;
    const timer = window.setTimeout(() => setAddition(null), 2400);
    return () => window.clearTimeout(timer);
  }, [addition]);

  const showAddedFeedback = (product: Product, origin?: DOMRect) => {
    const target = cartTarget.current?.getBoundingClientRect();
    setAddition({
      id: ++additionSequence.current, product,
      x: origin && origin.width ? origin.x + origin.width / 2 : window.innerWidth / 2,
      y: origin && origin.height ? origin.y + origin.height / 2 : window.innerHeight / 2,
      targetX: target && target.width ? target.x + target.width / 2 : window.innerWidth * .8,
      targetY: target && target.height ? target.y + target.height / 2 : window.innerHeight - 60,
    });
  };

  const IDLE_TIMEOUT = 240;
  const IDLE_WARNING = 30;

  const resetIdleTimer = useCallback(() => { setIdleSeconds(0); setShowIdleWarning(false); }, []);

  useEffect(() => {
    if (screen === 'attract' || screen === 'settings' || screen === 'success') return;
    const interval = setInterval(() => {
      setIdleSeconds(prev => {
        if (prev >= IDLE_TIMEOUT - IDLE_WARNING && !showIdleWarning) setShowIdleWarning(true);
        if (prev >= IDLE_TIMEOUT) { handleStartOver(); return 0; } // eslint-disable-line react-hooks/exhaustive-deps
        return prev + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, showIdleWarning]);

  useEffect(() => {
    const handleInteraction = () => resetIdleTimer();
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('click', handleInteraction);
    return () => { window.removeEventListener('touchstart', handleInteraction); window.removeEventListener('click', handleInteraction); };
  }, [resetIdleTimer]);

  // Data fetching
  const fetchCategories = async () => {
    try {
      const data = await productsAPI.getCategories(store!.id);
      setCategories(data.filter((c: Category) => c.is_active));
    } catch (e) { console.error('Failed to fetch categories:', e); }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getProducts(store!.id, { available_only: true });
      setProducts(data);
      setRecommendedProducts(data.filter((p: Product) => p.is_kiosk_recommended));
    } catch (e) { console.error('Failed to fetch products:', e); }
    finally { setLoading(false); }
  };

  const fetchTerminal = async () => {
    try {
      // Check if kiosk has assigned terminal
      const kioskTerminalId = (employee as any)?.kiosk_terminal_id;
      const res = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/terminals`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } });
      if (res.ok) {
        const terminals = await res.json();
        const active = terminals.filter((t: POSTerminal) => t.is_active);
        // Use assigned terminal or first active
        if (kioskTerminalId) {
          const assigned = active.find((t: POSTerminal) => t.id === kioskTerminalId);
          if (assigned) { setTerminal(assigned); return; }
        }
        if (active.length > 0) setTerminal(active[0]);
      }
    } catch (e) { console.error('Failed to fetch terminals:', e); }
  };

  const fetchDocumentTypes = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/fiscal/stores/${store?.id}/cashier-document-types`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } });
      if (res.ok) {
        const data = await res.json();
        setEcrEnabled(data.ecrEnabled);
        setDocumentTypes(data.documentTypes);
        if (data.documentTypes.length > 0) {
          const def = data.documentTypes.find((d: any) => d.name === 'ΑΠΟΔΕΙΞΗ ΛΙΑΝΙΚΗΣ ΠΩΛΗΣΗΣ');
          setSelectedDocumentTypeId(def?.id || data.documentTypes[0].id);
        }
      }
    } catch (e) { console.error('Failed to fetch document types:', e); }
  };

  const fetchProductMaterials = async (productId: number, sizeId?: number) => {
    setLoadingMaterials(true);
    try {
      if (!sizeId) {
        setProductIngredients([]);
        setAvailableExtras([]);
        setLoadingMaterials(false);
        return;
      }
      const url = `${API_BASE_URL}/api/products/${store?.id}/sizes/${sizeId}/ingredients`;
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` } });
      if (res.ok) {
        const data = await res.json();
        const ingredients = data.ingredients || [];
        setProductIngredients(ingredients);
        setAvailableExtras(ingredients.filter((m: any) => m.is_extra_available));
      }
    } catch (e) { console.error('Failed to fetch ingredients:', e); }
    finally { setLoadingMaterials(false); }
  };

  useEffect(() => { if (store?.id) { fetchCategories(); fetchProducts(); fetchTerminal(); fetchDocumentTypes(); } }, [store?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculations
  // Άθροισμα σε ακέραια λεπτά: γραμμή καταλόγου (κοινή με το ταμείο) + έξτρα.
  const calculateItemPrice = (product: Product, size: ProductSize | null, extras: typeof addedExtras): number => {
    const extrasCents = extras.reduce((sum, e) => sum + toCents(e.price) * e.quantity, 0);
    return (toCents(kioskLinePrice(product, size)) + extrasCents) / 100;
  };

  const calculateTotal = (): number => basket.reduce((total, item) => total + ((item.totalPrice || 0) * item.quantity), 0);
  const calculateVAT = (): number => basket.reduce((total, item) => { const rate = item.product.vat || 24; const itemTotal = (item.totalPrice || 0) * item.quantity; return total + (itemTotal - itemTotal / (1 + rate / 100)); }, 0);
  const totalItems = basket.reduce((sum, item) => sum + item.quantity, 0);

  // Basket management
  const addToBasket = (event?: React.MouseEvent<HTMLButtonElement>) => {
    if (!selectedProduct) return;
    const totalPrice = calculateItemPrice(selectedProduct, selectedSize, addedExtras);
    setBasket(prev => [...prev, { id: `${selectedProduct.id}-${Date.now()}`, product: selectedProduct, selectedSize, quantity: 1, removedIngredients, addedExtras: [...addedExtras], totalPrice }]);
    showAddedFeedback(selectedProduct, event?.currentTarget.getBoundingClientRect());
    closeCustomizeModal(); setScreen('menu');
  };

  const addProductDirectly = (product: Product, origin?: DOMRect) => {
    const totalPrice = kioskLinePrice(product, null);
    setBasket(prev => [...prev, { id: `${product.id}-${Date.now()}`, product, selectedSize: null, quantity: 1, removedIngredients: [], addedExtras: [], totalPrice }]);
    showAddedFeedback(product, origin);
  };

  const updateQuantity = (itemId: string, delta: number) => {
    setBasket(prev => prev.map(item => {
      if (item.id === itemId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null as any;
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromBasket = (itemId: string) => setBasket(prev => prev.filter(item => item.id !== itemId));

  // Product selection
  const handleProductClick = (product: Product, origin?: DOMRect) => {
    // Τέλος: όπως στο ταμείο, πάντα χωρίς μέγεθος, στο ποσό state_fees· χωρίς ποσό καμία
    // γραμμή (απόφαση 33) — αλλιώς ο πελάτης θα πλήρωνε 0,00 για σακούλα.
    if (isFeeProduct(product)) {
      if (feeUnitGross(product) !== null) addProductDirectly(product, origin);
      return;
    }
    setSelectedProduct(product); setSelectedSize(null); setRemovedIngredients([]); setAddedExtras([]);
    const hasSizes = (product.sizes?.length || 0) > 0;
    if (hasSizes || (product.ingredients?.length || 0) > 0) {
      if (hasSizes) { const def = defaultSize(product.sizes) ?? product.sizes![0]; setSelectedSize(def); fetchProductMaterials(product.id, def.id); }
      else fetchProductMaterials(product.id);
      setScreen('customize');
    } else {
      addProductDirectly(product, origin);
    }
  };

  const closeCustomizeModal = () => { setSelectedProduct(null); setSelectedSize(null); setRemovedIngredients([]); setAddedExtras([]); setProductIngredients([]); setAvailableExtras([]); };
  const handleSizeChange = (size: ProductSize) => { setSelectedSize(size); if (selectedProduct) fetchProductMaterials(selectedProduct.id, size.id); };
  const toggleIngredient = (id: number) => setRemovedIngredients(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleExtra = (extra: any) => setAddedExtras(prev => prev.find(e => e.raw_material_id === extra.raw_material_id) ? prev.filter(e => e.raw_material_id !== extra.raw_material_id) : [...prev, { raw_material_id: extra.raw_material_id, name: extra.raw_material_name, quantity: 1, price: Number(extra.extra_price) || 0 }]);

  // VAT Lookup (via shared hook)
  const lookupVatNumber = async () => {
    if (!invoiceVatNumber || invoiceVatNumber.length < 9) { setVatLookupError(t.vatNotFound); return; }
    const result = await (store?.id ? vatLookup(store.id) : Promise.resolve(null));
    if (!result) { if (!vatLookupError) setVatLookupError(t.vatNotFound); }
  };

  // Payment
  const handlePayment = async () => {
    if (!terminal) { alert(t.noTerminalConfigured); return; }
    if (documentType === 'invoice' && !invoiceCustomer) { setVatLookupError(t.lookupVatFirst); return; }

    setScreen('payment'); setPaymentStatus('processing'); setPaymentMessage(t.processing);
    try {
      const orderData = { orderType: 'takeaway', paymentMethod: 'card', items: basket, total: calculateTotal(), subtotal: calculateTotal() - calculateVAT(), vat: calculateVAT(), customer: documentType === 'invoice' && invoiceCustomer ? { name: invoiceCustomer.name, vat_number: invoiceCustomer.vat_number } : { name: t.kioskCustomer }, createdBy: employee?.id, storeId: store?.id, documentTypeId: ecrEnabled ? selectedDocumentTypeId : null, source: 'kiosk' };
      const orderRes = await fetch(`${API_BASE_URL}/api/orders/${store?.id}/takeaway`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }, body: JSON.stringify(orderData) });
      if (!orderRes.ok) throw new Error('Failed to create order');
      const orderResult = await orderRes.json();
      setSuccessOrderCode(orderResult.order.shortCode || orderResult.order.id.toString());

      setPaymentMessage(t.waitingForCard);
      const total = calculateTotal(), vatTotal = calculateVAT(), netTotal = total - vatTotal;
      const payRes = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/payment`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }, body: JSON.stringify({ terminalId: terminal.id, orderId: orderResult.order.id, amount: Math.round(total * 100), netAmount: Math.round(netTotal * 100), vatAmount: Math.round(vatTotal * 100), currency: 'EUR', documentTypeId: ecrEnabled ? selectedDocumentTypeId : null, requireSignature: ecrEnabled }) });
      // A proxy timeout on the long POS charge returns HTML, not JSON — parse defensively.
      let payData: any;
      try {
        payData = await payRes.json();
      } catch {
        setPaymentStatus('failed');
        setPaymentMessage((payRes.status === 504 || payRes.status === 502 || payRes.status === 408) ? 'Έληξε ο χρόνος αναμονής — παρακαλώ καλέστε προσωπικό.' : t.paymentFailed);
        return;
      }
      if (!payRes.ok || !payData.success) { setPaymentStatus('failed'); setPaymentMessage(payData.message || t.paymentFailed); return; }

      setPaymentStatus('success'); setPaymentMessage(t.paymentSuccess);

      // Generate fiscal document
      if (ecrEnabled && selectedDocumentTypeId) {
        if (documentType === 'invoice' && invoiceCustomer) {
          await generateInvoice(orderResult.order.id, payData);
        } else {
          await generateReceipt(orderResult.order.id, payData);
        }
      }

      setTimeout(() => { setScreen('success'); setTimeout(() => handleStartOver(), 5000); }, 1500);
    } catch (e) { console.error('Payment error:', e); setPaymentStatus('failed'); setPaymentMessage(e instanceof Error ? e.message : t.paymentFailed); }
  };

  const generateReceipt = async (orderId: number, paymentData: any) => {
    try {
      const orderItems = basket.map(item => ({ productId: item.product.id, productName: item.product.name, quantity: item.quantity, unitPrice: (item.totalPrice || 0) / item.quantity }));
      const total = calculateTotal(), vatTotal = calculateVAT(), netTotal = total - vatTotal;
      await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/receipt/pos`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }, body: JSON.stringify({ orderId, orderItems, totalNetAmount: netTotal, totalVatAmount: vatTotal, totalGrossAmount: total, posTransactionId: paymentData.transactionId, oxygenSignatureId: paymentData.signatureData?.signatureId, transactionId: paymentData.transactionId, signature: paymentData.signatureData?.signedText, invoiceSeries: paymentData.signatureData?.invoiceSeries, invoiceNumber: paymentData.signatureData?.invoiceNumber }) });
    } catch (e) { console.error('Error generating receipt:', e); }
  };

  const generateInvoice = async (orderId: number, paymentData: any) => {
    if (!invoiceCustomer) return;
    try {
      const orderItems = basket.map(item => ({ productId: item.product.id, productName: item.product.name, quantity: item.quantity, unitPrice: (item.totalPrice || 0) / item.quantity }));
      const total = calculateTotal(), vatTotal = calculateVAT(), netTotal = total - vatTotal;
      const selectedDoc = documentTypes.find(d => d.id === selectedDocumentTypeId);
      await fetch(`${API_BASE_URL}/api/stores/${store?.id}/fiscal/invoice`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }, body: JSON.stringify({ orderId, orderItems, totalNetAmount: netTotal, totalVatAmount: vatTotal, totalGrossAmount: total, documentTypeId: selectedDocumentTypeId, invoiceTypeCode: selectedDoc?.invoice_type_code, paymentMethodId: 7, customer: invoiceCustomer }) });
    } catch (e) { console.error('Error generating invoice:', e); }
  };

  const handleLogout = () => { authLogout(); window.location.href = '/'; };
  const handleStartOver = () => { setBasket([]); setAddition(null); setSelectedProduct(null); setSelectedCategory(null); setDocumentType('receipt'); resetVatLookup(); setPaymentStatus('idle'); setPaymentMessage(''); setSuccessOrderCode(''); setIdleSeconds(0); setShowIdleWarning(false); setShowResetConfirm(false); setLanguage('el'); setScreen('attract'); };

  // Το κιόσκι δεν έχει πληκτρολόγιο: το PIN προσωπικού μπαίνει από πλήκτρα αφής.
  const pressPinKey = (digit: string) => { setPinError(false); setSettingsPin(prev => (prev + digit).slice(0, 6)); };
  const erasePinKey = () => { setPinError(false); setSettingsPin(prev => prev.slice(0, -1)); };
  const confirmPin = () => {
    if (settingsPin.length >= 4) { setPinError(false); setPinUnlocked(true); }
    else { setPinError(true); setSettingsPin(''); setPinUnlocked(false); }
  };
  const leaveSettings = () => { setSettingsPin(''); setPinError(false); setPinUnlocked(false); setScreen('attract'); };

  const filteredProducts = selectedCategory ? products.filter(p => p.category_id === selectedCategory) : products;
  const idleRemaining = Math.max(0, IDLE_TIMEOUT - idleSeconds);

  // ========== ΚΟΙΝΑ ΚΟΜΜΑΤΙΑ ==========

  // Δύο ορατές γλώσσες αντί για toggle: ο πελάτης βλέπει τι θα πατήσει.
  const languagePills = (
    <div className="k-langs" role="group" aria-label="Language">
      {(['el', 'en'] as Language[]).map(l => (
        <button key={l} type="button" className="k-lang" aria-pressed={language === l}
          onClick={(e) => { e.stopPropagation(); setLanguage(l); }}>
          {l === 'el' ? 'ΕΛ' : 'EN'}
        </button>
      ))}
    </div>
  );

  const settingsButton = (
    <button type="button" className="k-iconbtn" aria-label={t.settings} title={t.staffOnly}
      onClick={(e) => { e.stopPropagation(); setScreen('settings'); }}>
      <Settings style={{ width: 22, height: 22 }} />
    </button>
  );

  const storeHeader = (title: string, sub?: string) => (
    <div>
      <h1 className="k-topbar__title">{title}</h1>
      {sub && <p className="k-topbar__sub">{sub}</p>}
    </div>
  );

  const brand = (
    <div className="k-brand">
      <span className="k-brand__mark"><UtensilsCrossed size={22} strokeWidth={1.7} /></span>
      <div><span className="k-brand__name">{store?.name || 'Kiosk'}</span><span className="k-brand__sub">{t.selfService}</span></div>
    </div>
  );

  const orderSteps = (active: number) => (
    <ol className="k-order-steps" aria-label={t.yourOrder}>
      {[t.chooseStep, t.reviewStep, t.payStep].map((label, index) => (
        <li key={label} className={index === active ? 'is-active' : index < active ? 'is-complete' : ''} aria-current={index === active ? 'step' : undefined}>
          <span>{index < active ? <Check size={13} /> : `0${index + 1}`}</span>{label}
        </li>
      ))}
    </ol>
  );

  // ========== RENDER SCREENS ==========

  // Check if kiosk is enabled for this store
  if (store && !store.kiosk_enabled) {
    return (
      <div className="kiosk" key={screen} lang={language}>

        <KioskBranding />
        <div className="k-center">
          <div className="k-status__icon"><Lock style={{ width: 52, height: 52 }} /></div>
          <h1 className="k-status__title">{t.kioskDisabledTitle}</h1>
          <p className="k-status__text">{t.kioskDisabledDesc}</p>
          <button onClick={handleLogout} className="k-btn k-btn--ink" style={{ marginTop: 32 }}>{t.backToLogin}</button>
        </div>
      </div>
    );
  }

  // ATTRACT SCREEN
  if (screen === 'attract') return (
    <KioskWelcome language={language} brand={brand} controls={<>{languagePills}{settingsButton}</>}
      products={[...recommendedProducts, ...products.filter(p => !p.is_kiosk_recommended)].map(p => ({ id: p.id, name: p.name, image_url: p.image_url, price: kioskTilePrice(p) }))}
      onStart={() => setScreen('menu')} />
  );

  // SETTINGS SCREEN — πληκτρολόγιο αφής, γιατί το κιόσκι δεν έχει πληκτρολόγιο.
  if (screen === 'settings') return (
    <div className="kiosk" key={screen} lang={language}>

      <KioskBranding />
      <div className="k-center">
        <div className="k-dialog k-in" style={{ maxWidth: 380 }}>
          <div className="k-status__icon" style={{ width: 64, height: 64, margin: '0 auto 16px' }}>
            <Lock style={{ width: 26, height: 26 }} />
          </div>
          <h2 className="k-dialog__title">{t.settings}</h2>
          <p className="k-dialog__text">{pinUnlocked ? t.staffOnly : t.enterPin}</p>

          <div className={`k-pin ${pinError ? 'k-pin--error' : ''}`} aria-label={t.enterPin}>
            {Array.from({ length: 6 }).map((_, i) => (
              <span key={i} className={`k-pin__dot ${i < settingsPin.length ? 'k-pin__dot--on' : ''}`} />
            ))}
          </div>
          {pinError && <p className="k-hint k-hint--error" style={{ marginTop: -16, marginBottom: 16 }}>{t.wrongPin}</p>}

          <div className="k-keypad">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
              <button key={d} type="button" className="k-key k-num" onClick={() => pressPinKey(d)}>{d}</button>
            ))}
            <button type="button" className="k-key k-key--fn" onClick={() => setSettingsPin('')} aria-label={t.clear}>C</button>
            <button type="button" className="k-key k-num" onClick={() => pressPinKey('0')}>0</button>
            <button type="button" className="k-key k-key--fn" onClick={erasePinKey} aria-label={t.back}>
              <Delete style={{ width: 24, height: 24 }} />
            </button>
          </div>

          <div className="k-stack">
            {!pinUnlocked && (
              <button onClick={confirmPin} disabled={settingsPin.length === 0} className="k-btn k-btn--primary k-btn--block">{t.confirm}</button>
            )}
            {pinUnlocked && (
              <button onClick={handleLogout} className="k-btn k-btn--danger k-btn--block">
                <LogOut style={{ width: 20, height: 20 }} />{t.logout}
              </button>
            )}
            <button onClick={leaveSettings} className="k-btn k-btn--quiet k-btn--block">{t.back}</button>
          </div>
        </div>
      </div>
    </div>
  );

  // SUCCESS SCREEN
  if (screen === 'success') return (
    <div className="kiosk" key={screen} lang={language}>

      <KioskBranding />
      <div className="k-center k-in">
        <div className="k-status__icon k-status__icon--ok"><Check style={{ width: 62, height: 62 }} strokeWidth={2.4} /></div>
        <h1 className="k-status__title">{t.thankYou}</h1>
        <div className="k-ticket">
          <p className="k-ticket__label">{t.orderNumber}</p>
          <p className="k-ticket__code k-num">{successOrderCode}</p>
        </div>
        <p className="k-status__text">{t.orderReady} · {t.enjoy}</p>
        <div className="k-progress" aria-hidden="true"><div className="k-progress__bar" /></div>
        <p className="k-hint">{t.newOrderSoon}</p>
      </div>
    </div>
  );

  // PAYMENT SCREEN
  if (screen === 'payment') return (
    <div className="kiosk" key={screen} lang={language}>
      <KioskBranding />
      <header className="k-topbar">{brand}{orderSteps(2)}</header>
      <div className="k-center">
        {paymentStatus === 'processing' && <div className="k-center k-in" style={{ padding: 0 }}>
          <div className="k-status__icon k-status__icon--wait">
            <Nfc style={{ width: 58, height: 58 }} />
          </div>
          <h1 className="k-status__title">{paymentMessage}</h1>
          <p className="k-status__text">{t.presentCard}</p>
          <div className="k-dots" style={{ marginTop: 24 }} aria-hidden="true"><span /><span /><span /></div>
          {terminal && <div className="k-terminal"><CreditCard style={{ width: 18, height: 18 }} />{t.terminalLabel}: {terminal.terminal_name}</div>}
          <p className="k-hint" style={{ marginTop: 16 }}>{t.dontRemoveCard}</p>
        </div>}

        {paymentStatus === 'success' && <div className="k-center k-in" style={{ padding: 0 }}>
          <div className="k-status__icon k-status__icon--ok"><Check style={{ width: 58, height: 58 }} strokeWidth={2.4} /></div>
          <h1 className="k-status__title">{t.paymentSuccess}</h1>
          <p className="k-status__text">{money(calculateTotal())}</p>
        </div>}

        {paymentStatus === 'failed' && <div className="k-center k-in" style={{ padding: 0 }}>
          <div className="k-status__icon k-status__icon--bad"><X style={{ width: 58, height: 58 }} strokeWidth={2.4} /></div>
          <h1 className="k-status__title">{t.paymentFailed}</h1>
          <p className="k-status__text">{paymentMessage}</p>
          <div className="k-stack" style={{ width: '100%', maxWidth: 360 }}>
            <button onClick={handlePayment} className="k-btn k-btn--primary k-btn--lg k-btn--block">{t.tryAgain}</button>
            <button onClick={() => setScreen('review')} className="k-btn k-btn--quiet k-btn--block">{t.back}</button>
          </div>
        </div>}
      </div>
    </div>
  );

  // REVIEW ORDER SCREEN
  if (screen === 'review') {
    const total = calculateTotal(), vat = calculateVAT();
    return (
      <div className="kiosk" key={screen} lang={language}>

        <KioskBranding />
        <div className="k-topbar">
          <button onClick={() => setScreen('menu')} className="k-back">
            <ChevronLeft style={{ width: 22, height: 22 }} />{t.back}
          </button>
          {storeHeader(t.reviewOrder, `${totalItems} ${totalItems === 1 ? t.item : t.items}`)}
          {orderSteps(1)}
          <div className="k-topbar__side">{languagePills}</div>
        </div>

        <div className="k-scroll">
          <div className="k-wrap">
            {basket.length === 0 ? (
              <div className="k-empty">
                <div className="k-empty__icon"><ShoppingCart style={{ width: 30, height: 30 }} /></div>
                {t.orderIsEmpty}
                <button onClick={() => setScreen('menu')} className="k-btn k-btn--ink" style={{ marginTop: 12 }}>{t.back}</button>
              </div>
            ) : (
              <div className="k-cols">
                {/* ── Αριστερά: γραμμές, προτεινόμενα, παραστατικό ── */}
                <div>
                  <div className="k-panel">
                    <div className="k-panel__body">
                      {basket.map(item => (
                        <div key={item.id} className="k-row">
                          <div className="k-row__thumb"><Thumb src={item.product.image_url} alt={item.product.name} /></div>
                          <div className="k-row__main">
                            <p className="k-row__name">{item.product.name}</p>
                            {/* F1-S1 (30/9, χωρίς διακόπτη): το ΜΟΝΑΔΙΚΟ πωλήσιμο μέγεθος («Κανονικό») δεν
                                γράφεται — ο πελάτης δεν το διάλεξε και θα το διάβαζε ως επιλογή· ίδιος κανόνας με
                                το καλάθι ταμείου (BasketItemRow) και το χαρτί. Εξαιρέσεις F1-W3-2/F1-W4-4 μέσα
                                στο hidesSizeName. Μόνο εμφάνιση: selectedSize/τιμή μένουν. */}
                            {item.selectedSize && !hidesSizeName(item.product, item.selectedSize.id) && <p className="k-row__meta">{item.selectedSize.name}</p>}
                            {item.addedExtras.length > 0 && <p className="k-row__meta">+ {item.addedExtras.map(e => e.name).join(', ')}</p>}
                          </div>
                          <div className="k-stepper">
                            <button className="k-step" onClick={() => updateQuantity(item.id, -1)} aria-label={`${t.decrease}: ${item.product.name}`}><Minus style={{ width: 18, height: 18 }} /></button>
                            <span className="k-stepper__value k-num">{item.quantity}</span>
                            <button className="k-step" onClick={() => updateQuantity(item.id, 1)} aria-label={`${t.increase}: ${item.product.name}`}><Plus style={{ width: 18, height: 18 }} /></button>
                          </div>
                          <p className="k-row__price k-num">{money((item.totalPrice || 0) * item.quantity)}</p>
                          <button onClick={() => removeFromBasket(item.id)} className="k-iconbtn k-iconbtn--plain" aria-label={`${t.remove}: ${item.product.name}`}>
                            <Trash2 style={{ width: 20, height: 20 }} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {recommendedProducts.length > 0 && (
                    <div style={{ marginTop: 28 }}>
                      <h2 className="k-section__title">{t.addMore}</h2>
                      <div className="k-suggest">
                        {recommendedProducts.slice(0, 8).map(product => (
                          <button key={product.id} onClick={() => handleProductClick(product)} className="k-suggest__item">
                            <div className="k-suggest__media"><Thumb src={product.image_url} alt={product.name} icon={22} /></div>
                            <div className="k-suggest__body">
                              <span className="k-suggest__name">{product.name}</span>
                              {kioskTilePrice(product) > 0 && <span className="k-suggest__price k-num">{money(kioskTilePrice(product))}</span>}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Δεξιά: παραστατικό + σύνοψη ── */}
                <div>
                  <div className="k-panel">
                    <div className="k-panel__body">
                      <p className="k-panel__title">{t.documentTitle}</p>
                      <div className="k-seg">
                        <button className="k-seg__btn" aria-pressed={documentType === 'receipt'}
                          onClick={() => { setDocumentType('receipt'); setInvoiceCustomer(null); setInvoiceVatNumber(''); setVatLookupError(null); }}>
                          <Receipt style={{ width: 19, height: 19 }} />{t.receipt}
                        </button>
                        <button className="k-seg__btn" aria-pressed={documentType === 'invoice'}
                          onClick={() => setDocumentType('invoice')}>
                          <FileText style={{ width: 19, height: 19 }} />{t.invoice}
                        </button>
                      </div>

                      {documentType === 'invoice' && (
                        <div style={{ marginTop: 20 }}>
                          <label className="k-label" htmlFor="k-vat">{t.enterVat}</label>
                          <div className="k-field">
                            <input id="k-vat" type="text" inputMode="numeric" className="k-input k-num" maxLength={9} placeholder="123456789"
                              value={invoiceVatNumber} onChange={(e) => setInvoiceVatNumber(e.target.value.replace(/\D/g, ''))} />
                            <button onClick={lookupVatNumber} disabled={lookingUpVat} className="k-btn k-btn--ink" aria-label={t.lookupVat}>
                              {lookingUpVat ? <Loader2 className="k-spin" style={{ width: 22, height: 22 }} /> : <Search style={{ width: 22, height: 22 }} />}
                            </button>
                          </div>
                          {vatLookupError && <p className="k-hint k-hint--error">{vatLookupError}</p>}
                          {invoiceCustomer && (
                            <div className="k-found">
                              <p className="k-found__name">{invoiceCustomer.name}</p>
                              <p className="k-found__meta">{t.vatNumberLabel}: {invoiceCustomer.vat_number}</p>
                              {invoiceCustomer.address && (
                                <p className="k-found__meta">{invoiceCustomer.address.street} {invoiceCustomer.address.number}, {invoiceCustomer.address.postal_code} {invoiceCustomer.address.city}</p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="k-panel">
                    <div className="k-panel__body">
                      <p className="k-panel__title">{t.summaryTitle}</p>
                      <div className="k-sum"><span className="k-sum__label">{t.subtotal}</span><span className="k-num">{money(total - vat)}</span></div>
                      <div className="k-sum"><span className="k-sum__label">{t.vat}</span><span className="k-num">{money(vat)}</span></div>
                      <div className="k-sum k-sum--total">
                        <span className="k-sum__label">{t.total}</span>
                        <span className="k-sum__value k-num">{money(total)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {basket.length > 0 && (
          <div className="k-actionbar">
            <div className="k-actionbar__meta">
              <p className="k-cart__label">{t.total}</p>
              <p className="k-cart__total k-num">{money(total)}</p>
            </div>
            <button onClick={handlePayment} disabled={documentType === 'invoice' && !invoiceCustomer}
              className="k-btn k-btn--primary k-btn--lg" style={{ flex: 1 }}>
              <CreditCard style={{ width: 24, height: 24 }} />{t.payNow}
            </button>
          </div>
        )}
      </div>
    );
  }

  // CUSTOMIZE SCREEN
  if (screen === 'customize' && selectedProduct) {
    const currentPrice = calculateItemPrice(selectedProduct, selectedSize, addedExtras);
    const baseIngredients = productIngredients.filter(m => !m.is_extra_available);
    return (
      <div className="kiosk" key={screen} lang={language}>

        <KioskBranding />
        <div className="k-topbar">
          <button onClick={() => { closeCustomizeModal(); setScreen('menu'); }} className="k-back">
            <ChevronLeft style={{ width: 22, height: 22 }} />{t.back}
          </button>
          {storeHeader(t.customize, t.customizeHint)}
          {orderSteps(0)}
          <div className="k-topbar__side">{languagePills}</div>
        </div>

        <div className="k-scroll">
          <div className="k-wrap">
            <div className="k-hero">
              <div className="k-hero__media"><Thumb src={selectedProduct.image_url} alt={selectedProduct.name} icon={48} /></div>
              <div>
                <h2 className="k-hero__name">{selectedProduct.name}</h2>
                {selectedProduct.description && <p className="k-hero__desc">{selectedProduct.description}</p>}
                <p className="k-hero__price k-num">{money(currentPrice)}</p>

                {(selectedProduct.sizes?.length || 0) > 0 && (
                  <div style={{ marginTop: 28 }}>
                    <p className="k-panel__title">{t.selectSize}</p>
                    <div className="k-sizes">
                      {/* Κάθε επιλογή δείχνει την ΤΕΛΙΚΗ τιμή της γραμμής (χωρίς έξτρα), όχι
                          «+προσαύξηση»: στο μοντέλο «η τιμή ζει στο μέγεθος» το μέγεθος είναι
                          ολόκληρη η τιμή, και ένα «+3,00» θα διαβαζόταν ως επιπλέον χρέωση. */}
                      {kioskSizeOptions(selectedProduct).map(size => (
                        <button key={size.id} className="k-size" aria-pressed={selectedSize?.id === size.id} onClick={() => handleSizeChange(size)}>
                          <span className="k-size__name">{size.name}</span>
                          <span className="k-size__mod k-num">{money(kioskLinePrice(selectedProduct, size))}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loadingMaterials ? (
              <div className="k-empty"><Loader2 className="k-spin" style={{ width: 32, height: 32 }} /></div>
            ) : (
              <>
                {baseIngredients.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <p className="k-panel__title">{t.ingredients}</p>
                    {baseIngredients.map(ing => {
                      const removed = removedIngredients.includes(ing.raw_material_id);
                      return (
                        <button key={ing.raw_material_id} onClick={() => toggleIngredient(ing.raw_material_id)}
                          className={`k-option ${removed ? 'k-option--removed' : 'k-option--kept'}`} aria-pressed={!removed}>
                          <span className="k-option__box">
                            {removed ? <X style={{ width: 16, height: 16 }} strokeWidth={3} /> : <Check style={{ width: 16, height: 16 }} strokeWidth={3} />}
                          </span>
                          <span className="k-option__text">{ing.raw_material_name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {availableExtras.length > 0 && (
                  <div style={{ marginTop: 32 }}>
                    <p className="k-panel__title">{t.extras}</p>
                    {availableExtras.map(extra => {
                      const isAdded = addedExtras.some(e => e.raw_material_id === extra.raw_material_id);
                      return (
                        <button key={extra.raw_material_id} onClick={() => toggleExtra(extra)}
                          className={`k-option ${isAdded ? 'k-option--on' : ''}`} aria-pressed={isAdded}>
                          <span className="k-option__box">
                            {isAdded ? <Check style={{ width: 16, height: 16 }} strokeWidth={3} /> : <Plus style={{ width: 16, height: 16 }} strokeWidth={3} />}
                          </span>
                          <span className="k-option__text">{extra.raw_material_name}</span>
                          <span className="k-option__price k-num">+{money(Number(extra.extra_price || 0))}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="k-actionbar">
          <div className="k-actionbar__meta">
            <p className="k-cart__label">{t.total}</p>
            <p className="k-cart__total k-num">{money(currentPrice)}</p>
          </div>
          <button onClick={addToBasket} className="k-btn k-btn--primary k-btn--lg" style={{ flex: 1 }}>
            <Plus style={{ width: 24, height: 24 }} />{t.addToOrder}
          </button>
        </div>
      </div>
    );
  }

  // MAIN MENU SCREEN
  return (
    <div className="kiosk k-menu" lang={language} key="menu">
      <KioskBranding />
      {addition && <>
        <div key={`flight-${addition.id}`} className="k-flight" aria-hidden="true" style={{
          '--fly-x': `${addition.x - 38}px`, '--fly-y': `${addition.y - 38}px`,
          '--fly-end-x': `${addition.targetX - 38}px`, '--fly-end-y': `${addition.targetY - 38}px`,
        } as React.CSSProperties}><Thumb src={addition.product.image_url} alt="" /><span><Check size={26} /></span></div>
        <div key={`notice-${addition.id}`} className="k-added-notice" role="status"><span><Check size={24} /></span><div><strong>{t.addedToBag}</strong><p>{addition.product.name}</p></div></div>
      </>}
      {/* Idle Warning */}
      {showIdleWarning && (
        <div className="k-overlay" role="dialog" aria-modal="true">
          <div className="k-dialog">
            <div className="k-status__icon" style={{ width: 72, height: 72, margin: '0 auto 20px' }}>
              <AlertCircle style={{ width: 32, height: 32 }} />
            </div>
            <h2 className="k-dialog__title">{t.sessionExpiring}</h2>
            <p className="k-dialog__text">{t.idleText} <strong className="k-num">{idleRemaining}</strong> {t.seconds}</p>
            <div className="k-stack">
              <button onClick={resetIdleTimer} className="k-btn k-btn--primary k-btn--lg k-btn--block">{t.continueOrder}</button>
              <button onClick={handleStartOver} className="k-btn k-btn--quiet k-btn--block">{t.startOver}</button>
            </div>
          </div>
        </div>
      )}

      {showResetConfirm && (
        <div className="k-overlay" role="dialog" aria-modal="true" aria-labelledby="k-reset-title">
          <div className="k-dialog">
            <h2 id="k-reset-title" className="k-dialog__title">{t.resetTitle}</h2>
            <p className="k-dialog__text">{t.resetDesc}</p>
            <div className="k-stack">
              <button className="k-btn k-btn--primary" onClick={() => setShowResetConfirm(false)} autoFocus>{t.continueOrder}</button>
              <button className="k-btn k-btn--quiet" onClick={handleStartOver}>{t.resetOrder}</button>
            </div>
          </div>
        </div>
      )}

      <header className="k-topbar k-menu__header">
        {brand}{orderSteps(0)}
        <div className="k-topbar__side">
          <button className="k-iconbtn k-mobile-reset" aria-label={t.resetOrder} onClick={() => basket.length ? setShowResetConfirm(true) : handleStartOver()}><RotateCcw size={19} /></button>
          {languagePills}{settingsButton}
        </div>
      </header>

      <div className="k-menu__layout">
        <nav className="k-sidebar" aria-label={t.categoriesLabel}>
          <p className="k-sidebar__label">{t.menuLabel}</p>
          <div className="k-rail">
            <button className="k-chip" aria-pressed={selectedCategory === null} onClick={() => setSelectedCategory(null)}>
              <span className="k-chip__symbol"><LayoutGrid size={20} /></span><span>{t.allCategories}</span><small>{products.length}</small>
            </button>
            {categories.map((cat, index) => (
              <button key={cat.id} className="k-chip" aria-pressed={selectedCategory === cat.id} onClick={() => setSelectedCategory(cat.id)}>
                <span className="k-chip__symbol k-num">{String(index + 1).padStart(2, '0')}</span>
                <span>{cat.name}</span><small>{products.filter(p => p.category_id === cat.id).length}</small>
              </button>
            ))}
          </div>
          <div className="k-sidebar__bottom">
            <button className="k-reset" onClick={() => basket.length ? setShowResetConfirm(true) : handleStartOver()}><RotateCcw size={17} />{t.resetOrder}</button>
          </div>
        </nav>

        <main className="k-catalog k-scroll" key={selectedCategory ?? 'all'}>
          <div className="k-catalog__heading"><div><p className="k-eyebrow">{t.browseHint}</p><h1>{t.choosePrompt}</h1></div><span className="k-catalog__spark"><Sparkles size={32} strokeWidth={1.4} /></span></div>
          <div className="k-catalog__section"><h2>{categories.find(c => c.id === selectedCategory)?.name || t.menuLabel}</h2><span>{filteredProducts.length} {t.items}</span></div>
          {loading ? (
            <div className="k-grid" aria-busy="true" aria-label={t.processing}>
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="k-skel"><div className="k-skel__media k-shimmer" /><div className="k-skel__line" /><div className="k-skel__line k-skel__line--short" /></div>)}
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="k-empty"><div className="k-empty__icon"><UtensilsCrossed size={30} /></div>{t.noProducts}</div>
          ) : (
            <div className="k-grid">
              {filteredProducts.map((product, index) => {
                const quantity = basket.filter(item => item.product.id === product.id).reduce((sum, item) => sum + item.quantity, 0);
                return (
                  <button key={product.id} onClick={event => handleProductClick(product, event.currentTarget.getBoundingClientRect())}
                    style={{ '--card-delay': `${Math.min(index, 7) * 45}ms` } as React.CSSProperties}
                    className={`k-card ${quantity ? 'k-card--added' : ''}`}>
                    <div className="k-card__media">
                      <Thumb src={product.image_url} alt={product.name} icon={38} />
                      {product.is_kiosk_recommended && <span className="k-tag"><Sparkles size={12} />{t.popular}</span>}
                      {quantity > 0 && <span key={quantity} className="k-card__quantity">{quantity}<span className="k-sr-only"> {t.added}</span></span>}
                    </div>
                    <div className="k-card__body">
                      <span className="k-card__name">{product.name}</span>
                      {product.description && <span className="k-card__desc">{product.description}</span>}
                      <div className="k-card__bottom"><span className="k-card__price k-num">{money(kioskTilePrice(product))}</span><span className="k-card__add"><Plus size={22} /></span></div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </main>

      </div>
      <aside className={`k-checkout ${basket.length ? 'k-checkout--filled' : ''}`} aria-label={t.yourOrder}>
        <span className="k-checkout__bag" key={totalItems}><ShoppingBag size={30} /><span className="k-checkout__count" aria-live="polite">{totalItems}</span></span>
        <div className="k-checkout__summary"><strong>{t.yourBag}</strong><span>{basket.length ? `${totalItems} ${totalItems === 1 ? t.item : t.items}` : t.startHere}</span></div>
        <div className="k-checkout__previews" aria-hidden="true">{basket.slice(-3).map(item => <span key={item.id}><Thumb src={item.product.image_url} alt="" /></span>)}</div>
        <div className="k-checkout__total"><span>{t.total}</span><strong className="k-num" key={calculateTotal()}>{money(calculateTotal())}</strong></div>
        <button ref={cartTarget} className="k-btn k-btn--primary k-checkout__button" disabled={basket.length === 0} onClick={() => { setAddition(null); setScreen('review'); }}>{t.viewOrder}<ArrowRight size={28} /></button>
      </aside>
    </div>
  );
};

export default SelfCheckoutKiosk;
